// PATH: src/pages/admin/AdminTabs.jsx
// VERSION: 1.4 — Agrega contador de visitas en el header (lee /metrics/visits)

import React, { useEffect, useState } from 'react';
import {
  Box,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Heading,
  Text,
  Icon,
  Flex,
  Button,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  FiBookOpen,
  FiBook,
  FiFeather,
  FiLink,
  FiUsers,
  FiVideo,
  FiImage,
  FiCalendar,
  FiClock,
  FiLogOut,
  FiMail,
  FiZap,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { API } from '../../utils/apiBase';

import VersesTab from './tabs/VersesTab';
import StudiesTab from './tabs/StudiesTab';
import DevotionalsTab from './tabs/DevotionalsTab';
import LinksTab from './tabs/LinksTab';
import MinistryTab from './tabs/MinistryTab';
import MediaTab from './tabs/MediaTab';
import AlbumsTab from './tabs/AlbumsTab';
import EventsTab from './tabs/EventsTab';
import FixedEventsTab from './tabs/FixedEventsTab';
import NewsletterTab from './tabs/NewsletterTab';
import AIGeneratorTab from './tabs/AIGeneratorTab';


export default function AdminTabs() {
  const navigate = useNavigate();
  const { logout } = useAdminAuth();

  const bgBox = useColorModeValue('white', 'bg.card');
  const borderColor = useColorModeValue('brand.200', 'border.default');
  const textColor = useColorModeValue('brand.800', 'text.default');
  const subTextColor = useColorModeValue('brand.500', 'text.muted');
  const visitTextColor = useColorModeValue('brand.400', 'brand.300');
  const tabListBorderColor = useColorModeValue('brand.100', 'border.default');

  const tabStyle = {
    fontWeight: 'semibold',
    fontSize: 'sm',
    px: 3,
    py: 2,
    borderRadius: 'lg',
    color: useColorModeValue('brand.600', 'text.muted'),
    border: '1px solid',
    borderColor: useColorModeValue('brand.200', 'border.default'),
    bg: useColorModeValue('white', 'whiteAlpha.50'),
    _selected: {
      bg: 'accent.500',
      color: 'white',
      borderColor: 'accent.500',
      boxShadow: 'sm',
    },
    _hover: {
      bg: useColorModeValue('brand.100', 'whiteAlpha.200'),
      color: useColorModeValue('brand.800', 'text.default'),
      transform: 'translateY(-1px)',
      boxShadow: 'sm',
    },
    transition: 'all 0.15s ease-out',
  };

  const [visitCount, setVisitCount] = useState(null);
  const [visitsError, setVisitsError] = useState(null);

  const handleLogout = () => {
    logout();
    navigate('/admin/login', { replace: true });
  };

  // Cargar contador de visitas desde el backend
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setVisitsError(null);
        const res = await fetch(`${API}/metrics/visits`);
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = await res.json();
        if (!alive) return;

        const raw = data?.value ?? data?.VALUE ?? 0;
        const num = Number(raw);
        setVisitCount(Number.isFinite(num) ? num : 0);
      } catch (e) {
        if (alive) {
          setVisitsError(e?.message || 'Error al cargar visitas');
        }
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  const visitasLabel = visitsError
    ? '—'
    : visitCount === null
      ? 'cargando…'
      : visitCount.toLocaleString('es-HN');

  return (
    <Box p={{ base: 4, md: 6 }}>
      <Box
        bg={bgBox}
        borderRadius="xl"
        border="1px solid"
        borderColor={borderColor}
        boxShadow="sm"
        p={{ base: 4, md: 6 }}
      >
        {/* Header con botón de cierre */}
        <Flex align="center" justify="space-between" mb={2} gap={3} wrap="wrap">
          <Box>
            <Heading fontSize="2xl" mb={1} color={textColor}>
              Panel de Administración
            </Heading>
            <Text fontSize="sm" color={subTextColor}>
              Gestiona el contenido visible en la web: versos, estudios, devocionales, enlaces,
              ministerio, media, álbumes, eventos y horarios.
            </Text>
            <Text fontSize="xs" color={visitTextColor} mt={1}>
              Visitas al sitio: <strong>{visitasLabel}</strong>
            </Text>
          </Box>
          <Button
            onClick={handleLogout}
            leftIcon={<FiLogOut />}
            colorScheme="red"
            variant="solid"
            size="sm"
          >
            Cerrar administrador
          </Button>
        </Flex>

        <Tabs variant="unstyled" isFitted>
          <TabList
            flexWrap="wrap"
            gap={2}
            borderBottom="1px solid"
            borderColor={tabListBorderColor}
            pb={2}
            mb={3}
          >
            <Tab {...tabStyle}>
              <Flex align="center" gap={2}>
                <Icon as={FiBookOpen} />
                <span>Versos</span>
              </Flex>
            </Tab>
            <Tab {...tabStyle}>
              <Flex align="center" gap={2}>
                <Icon as={FiBook} />
                <span>Estudios</span>
              </Flex>
            </Tab>
            <Tab {...tabStyle}>
              <Flex align="center" gap={2}>
                <Icon as={FiFeather} />
                <span>Devocionales</span>
              </Flex>
            </Tab>
            <Tab {...tabStyle}>
              <Flex align="center" gap={2}>
                <Icon as={FiLink} />
                <span>Enlaces</span>
              </Flex>
            </Tab>
            <Tab {...tabStyle}>
              <Flex align="center" gap={2}>
                <Icon as={FiUsers} />
                <span>Ministerio</span>
              </Flex>
            </Tab>
            <Tab {...tabStyle}>
              <Flex align="center" gap={2}>
                <Icon as={FiVideo} />
                <span>Media</span>
              </Flex>
            </Tab>
            <Tab {...tabStyle}>
              <Flex align="center" gap={2}>
                <Icon as={FiImage} />
                <span>Álbumes</span>
              </Flex>
            </Tab>
            <Tab {...tabStyle}>
              <Flex align="center" gap={2}>
                <Icon as={FiCalendar} />
                <span>Eventos</span>
              </Flex>
            </Tab>
            <Tab {...tabStyle}>
              <Flex align="center" gap={2}>
                <Icon as={FiClock} />
                <span>Horarios</span>
              </Flex>
            </Tab>
            <Tab {...tabStyle}>
              <Flex align="center" gap={2}>
                <Icon as={FiMail} />
                <span>Boletín</span>
              </Flex>
            </Tab>
            <Tab {...tabStyle}>
              <Flex align="center" gap={2}>
                <Icon as={FiZap} />
                <span>Generador IA</span>
              </Flex>
            </Tab>
          </TabList>

          <TabPanels mt={2}>
            <TabPanel px={0}>
              <VersesTab />
            </TabPanel>
            <TabPanel px={0}>
              <StudiesTab />
            </TabPanel>
            <TabPanel px={0}>
              <DevotionalsTab />
            </TabPanel>
            <TabPanel px={0}>
              <LinksTab />
            </TabPanel>
            <TabPanel px={0}>
              <MinistryTab />
            </TabPanel>
            <TabPanel px={0}>
              <MediaTab />
            </TabPanel>
            <TabPanel px={0}>
              <AlbumsTab />
            </TabPanel>
            <TabPanel px={0}>
              <EventsTab />
            </TabPanel>
            <TabPanel px={0}>
              <FixedEventsTab />
            </TabPanel>
            <TabPanel px={0}>
              <NewsletterTab />
            </TabPanel>
            <TabPanel px={0}>
              <AIGeneratorTab />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </Box>
  );
}
